/**
 * Rubber gym flooring area calculator.
 * Reads per-variant pricing from the rendered JSON, computes tiles needed
 * (area + waste, rounded up), applies the bulk-discount tiers, and adds the
 * selected-thickness variant to the cart via the theme's AJAX cart.
 */
(function () {
  var BULK_TIERS = [
    { min: 200, pct: 25 },
    { min: 100, pct: 20 },
    { min: 50, pct: 15 },
    { min: 25, pct: 10 },
    { min: 10, pct: 5 }
  ];
  var VAT = 1.2;
  // Interlocking connectors: recommend 3 per 1m x 1m tile, but only for the
  // thicker mats (20/30/40mm). 10mm mats don't take connectors.
  var CONNECTORS_PER_TILE = 3;
  var CONNECTOR_MIN_THICKNESS = 20;

  function money(cents) {
    var n = Math.round(cents) / 100;
    return '£' + n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  function bulkFor(qty) {
    for (var i = 0; i < BULK_TIERS.length; i++) {
      if (qty >= BULK_TIERS[i].min) return BULK_TIERS[i];
    }
    return null;
  }

  // Cheapest combination of connector packs (e.g. 1 / 4 / 100) that covers at
  // least `n` connectors. Min-cost coin-change DP that allows a small overshoot
  // (up to the largest pack) when a bulk pack works out cheaper than buying the
  // exact remainder in singles. Returns { units, cost, packs:[{id,qty,units}] }.
  function cheapestMix(packVariants, n) {
    var empty = { units: 0, cost: 0, packs: [] };
    if (!packVariants.length || n <= 0) return empty;

    var maxUnits = 0;
    packVariants.forEach(function (p) {
      if (p.units > maxUnits) maxUnits = p.units;
    });
    var cap = n + maxUnits;
    var dp = new Array(cap + 1).fill(Infinity);
    var pick = new Array(cap + 1).fill(-1);
    dp[0] = 0;
    for (var j = 1; j <= cap; j++) {
      for (var k = 0; k < packVariants.length; k++) {
        var u = packVariants[k].units;
        if (u <= j && dp[j - u] + packVariants[k].price < dp[j]) {
          dp[j] = dp[j - u] + packVariants[k].price;
          pick[j] = k;
        }
      }
    }

    // Best total lies in [n, n + maxUnits - 1]: overshooting by a whole pack
    // can never be cheaper, so we never look past it.
    var bestJ = -1;
    var bestCost = Infinity;
    for (var t = n; t <= n + maxUnits - 1 && t <= cap; t++) {
      if (dp[t] < bestCost) {
        bestCost = dp[t];
        bestJ = t;
      }
    }
    if (bestJ < 0) return empty;

    var counts = {};
    var node = bestJ;
    while (node > 0 && pick[node] >= 0) {
      var idx = pick[node];
      counts[idx] = (counts[idx] || 0) + 1;
      node -= packVariants[idx].units;
    }

    var packs = [];
    var units = 0;
    Object.keys(counts).forEach(function (idx) {
      var p = packVariants[idx];
      packs.push({ id: p.id, units: p.units, qty: counts[idx] });
      units += p.units * counts[idx];
    });
    return { units: units, cost: bestCost, packs: packs };
  }

  function cartAddUrl() {
    var root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return root + 'cart/add.js';
  }

  // True when a third-party cart drawer (e.g. UpCart) is handling the cart.
  // Such apps intercept the /cart/add.js request and open their own drawer,
  // so we must NOT open the theme drawer or redirect ourselves.
  function thirdPartyCartActive() {
    return !!(
      window.upcartSubscribeCartLoaded ||
      window.upcartDocumentOrShadowRoot ||
      window.upcartOnCartLoaded ||
      document.querySelector('#upcart-cart-container, [id^="upcart"], upcart-cart')
    );
  }

  function notifyCartUpdated(variantId, cart) {
    if (window.MinimogEvents && window.MinimogTheme && MinimogTheme.pubSubEvents) {
      MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { cart: cart, productVariantId: variantId });
    }
    document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { variantId: variantId } }));
  }

  function openCartUi() {
    // Let UpCart / other ajax-intercepting drawers open themselves.
    if (thirdPartyCartActive()) return;
    var drawer = document.querySelector('m-cart-drawer');
    if (drawer && typeof drawer.open === 'function' && !(window.MinimogSettings && MinimogSettings.enable_cart_drawer === false)) {
      try {
        drawer.open();
      } catch (e) {
        /* no-op */
      }
    }
  }

  function init(widget) {
    if (widget.dataset.floorInit) return;
    widget.dataset.floorInit = '1';

    var variants = [];
    var dataEl = widget.querySelector('[data-floor-variants]');
    try {
      variants = JSON.parse(dataEl.textContent) || [];
    } catch (e) {
      variants = [];
    }
    var byId = {};
    variants.forEach(function (v) {
      byId[String(v.id)] = v;
    });

    var connectors = [];
    var connEl = widget.querySelector('[data-connector-variants]');
    if (connEl) {
      try {
        connectors = JSON.parse(connEl.textContent) || [];
      } catch (e) {
        connectors = [];
      }
    }
    connectors = connectors.filter(function (c) {
      return c && c.available && c.units > 0 && c.id && c.price >= 0;
    });

    var lengthEl = widget.querySelector('[data-floor-length]');
    var widthEl = widget.querySelector('[data-floor-width]');
    var thicknessEl = widget.querySelector('[data-floor-thickness]');
    var wasteEl = widget.querySelector('[data-floor-waste]');
    var results = widget.querySelector('[data-floor-results]');
    var addBtn = widget.querySelector('[data-floor-add]');
    var addText = widget.querySelector('[data-floor-add-text]');

    var out = {
      area: widget.querySelector('[data-floor-area]'),
      waste: widget.querySelector('[data-floor-waste-out]'),
      tiles: widget.querySelector('[data-floor-tiles]'),
      perTile: widget.querySelector('[data-floor-per-tile]'),
      was: widget.querySelector('[data-floor-was]'),
      total: widget.querySelector('[data-floor-total]'),
      totalEx: widget.querySelector('[data-floor-total-ex]'),
      saving: widget.querySelector('[data-floor-saving]'),
      connRow: widget.querySelector('[data-connector-row]'),
      connOut: widget.querySelector('[data-connector-out]')
    };

    var state = { tiles: 0, variantId: null, connectorItems: [] };

    function recalc() {
      var l = parseFloat(lengthEl.value);
      var w = parseFloat(widthEl.value);
      var waste = parseFloat(wasteEl.value) || 0;
      var variant = byId[thicknessEl.value];

      var valid = !isNaN(l) && l > 0 && !isNaN(w) && w > 0 && variant;
      if (!valid) {
        results.hidden = true;
        addBtn.disabled = true;
        addText.textContent = 'Enter your room size';
        return;
      }

      var area = l * w;
      var coverTiles = Math.ceil(area);
      var tiles = Math.ceil(area * (1 + waste));
      var spare = Math.max(0, tiles - coverTiles);
      var perTile = variant.price;
      var subtotal = tiles * perTile;

      var tier = bulkFor(tiles);
      var total = subtotal;
      if (tier) total = subtotal * (1 - tier.pct / 100);

      // Interlocking connectors: 3 per tile on 20/30/40mm mats only, sourced as
      // the cheapest mix of connector packs.
      var thicknessNum = parseInt(String(variant.thickness), 10);
      var needsConnectors =
        connectors.length > 0 && !isNaN(thicknessNum) && thicknessNum >= CONNECTOR_MIN_THICKNESS;
      var connectorTarget = needsConnectors ? tiles * CONNECTORS_PER_TILE : 0;
      var mix = cheapestMix(connectors, connectorTarget);
      var connectorCost = mix.cost;
      var grandTotal = total + connectorCost;

      out.area.textContent = area.toLocaleString('en-GB', { maximumFractionDigits: 2 }) + ' m²';
      out.waste.textContent = Math.round(waste * 100) + '% (+' + spare + ' tile' + (spare === 1 ? '' : 's') + ')';
      out.tiles.textContent = tiles.toLocaleString('en-GB');
      out.perTile.textContent = money(perTile);
      out.total.textContent = money(grandTotal);
      out.totalEx.textContent = money(grandTotal / VAT);

      if (out.connRow) {
        if (mix.packs.length) {
          out.connRow.hidden = false;
          out.connOut.textContent =
            mix.units.toLocaleString('en-GB') +
            ' connector' + (mix.units === 1 ? '' : 's') +
            ' · ' + money(connectorCost);
        } else {
          out.connRow.hidden = true;
        }
      }

      if (tier) {
        out.was.textContent = money(subtotal + connectorCost);
        out.was.hidden = false;
        out.saving.hidden = false;
        out.saving.textContent =
          'Bulk Discount: ' + tier.pct + '% off ' + tier.min + '+ tiles — you save ' + money(subtotal - total);
      } else {
        out.was.hidden = true;
        out.saving.hidden = true;
      }

      results.hidden = false;
      state.tiles = tiles;
      state.variantId = variant.id;
      state.connectorItems = mix.packs.map(function (p) {
        return { id: p.id, quantity: p.qty };
      });

      if (!variant.available) {
        addBtn.disabled = true;
        addText.textContent = 'Selected thickness unavailable';
      } else {
        addBtn.disabled = false;
        addText.textContent =
          'Add ' + tiles + ' tile' + (tiles === 1 ? '' : 's') +
          (state.connectorItems.length ? ' + connectors' : '') + ' to basket';
      }
    }

    [lengthEl, widthEl, thicknessEl, wasteEl].forEach(function (el) {
      el.addEventListener('input', recalc);
      el.addEventListener('change', recalc);
    });

    addBtn.addEventListener('click', function () {
      if (!state.variantId || state.tiles < 1) return;
      addBtn.disabled = true;
      var original = addText.textContent;
      addText.textContent = 'Adding…';

      function reset(text, delay) {
        addText.textContent = text;
        setTimeout(function () {
          addText.textContent = original;
          addBtn.disabled = false;
        }, delay || 1500);
      }

      // Add the mat plus any recommended connector packs in one request. Cart-
      // drawer apps such as UpCart intercept this and open their sidebar
      // automatically — so we never redirect to the cart page.
      // Add connector packs first so the mat is the most-recently-added line —
      // the cart drawer shows newest at the top, keeping the flooring on top.
      var items = (state.connectorItems || []).concat([{ id: state.variantId, quantity: state.tiles }]);
      fetch(cartAddUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          return r.json().then(function (body) {
            return { ok: r.ok, body: body };
          });
        })
        .then(function (res) {
          if (!res.ok || (res.body && res.body.status)) {
            reset((res.body && (res.body.description || res.body.message)) || 'Could not add', 2500);
            return;
          }
          notifyCartUpdated(state.variantId, res.body);
          openCartUi();
          reset('Added ✓');
        })
        .catch(function (err) {
          console.error('Flooring calculator add failed', err);
          reset('Could not add', 2500);
        });
    });

    recalc();
  }

  function boot() {
    document.querySelectorAll('[data-floor-calc]').forEach(init);
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
